<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  installment: { type: Object, default: null },
  initialStatus: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
  statuses: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const targetStatus = ref('');
const reason = ref('');
const canceledOn = ref('');
const options = computed(() => props.statuses);
const canceling = computed(() => targetStatus.value === 'cancelado');
const propertyAddress = computed(
  () => props.installment?.parcela?.endereco || {}
);
const cityLine = computed(() =>
  [propertyAddress.value.bairro, propertyAddress.value.cidade]
    .filter(Boolean)
    .join(' - ')
);
const streetLine = computed(() => {
  const street = [
    propertyAddress.value.logradouro,
    propertyAddress.value.numero,
  ]
    .filter(Boolean)
    .join(', ');
  const details = [
    propertyAddress.value.unidade,
    propertyAddress.value.complemento,
  ]
    .filter(Boolean)
    .join(' - ');
  return [street, details].filter(Boolean).join(' - ');
});
const managerName = computed(
  () =>
    props.installment?.dados_processo?.manager?.name ||
    props.installment?.dados_processo?.gerente_name ||
    ''
);
const modalHeaderClass = computed(() =>
  canceling.value
    ? 'flex shrink-0 justify-end px-4 pt-4 sm:px-8 sm:pt-8'
    : undefined
);
const modalContentClass = computed(() =>
  canceling.value
    ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:min-h-[500px] sm:px-8 sm:py-6'
    : undefined
);
const valid = computed(
  () =>
    targetStatus.value &&
    (!canceling.value || (reason.value.trim() && canceledOn.value))
);

watch(
  () => props.show,
  show => {
    if (show) {
      targetStatus.value = props.initialStatus;
      reason.value = '';
      canceledOn.value = '';
    }
  },
  { immediate: true }
);

const confirm = () => {
  if (!valid.value) return;
  emit('confirm', {
    target_status: targetStatus.value,
    reason: reason.value.trim(),
    canceled_on: canceledOn.value,
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="canceling ? '' : t('REAL_ESTATE_FINANCE.STATUS.TITLE')"
    :max-width-class="canceling ? 'max-w-[806px]' : 'max-w-[640px]'"
    :header-class="modalHeaderClass"
    :content-class="modalContentClass"
    @close="emit('close')"
  >
    <template v-if="canceling" #header>
      <span aria-hidden="true" />
    </template>
    <div class="flex flex-col gap-6">
      <h3 v-if="canceling" class="font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.STATUS.CANCEL_CONFIRMATION') }}
      </h3>
      <p v-else class="text-sm text-lais-text-muted">
        {{ installment?.dados_processo?.titulo }}
      </p>
      <div
        v-if="canceling"
        class="flex flex-col gap-1 text-sm text-lais-text-muted"
      >
        <p class="flex items-center gap-2">
          <span
            class="i-lucide-map-pin size-4 text-lais-primary"
            aria-hidden="true"
          />
          <span>
            {{ cityLine || t('REAL_ESTATE_FINANCE.TABLE.ADDRESS_UNAVAILABLE') }}
          </span>
        </p>
        <p class="pl-6">{{ streetLine }}</p>
      </div>
      <p v-if="canceling && managerName" class="text-sm text-lais-text-muted">
        {{ t('REAL_ESTATE_FINANCE.STATUS.MANAGER_LABEL') }}
        <strong>{{ managerName }}</strong>
      </p>
      <LaisField
        v-if="!initialStatus"
        :label="t('REAL_ESTATE_FINANCE.STATUS.FIELD')"
        required
      >
        <select
          v-model="targetStatus"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
        >
          <option value="">
            {{ t('REAL_ESTATE_FINANCE.STATUS.SELECT') }}
          </option>
          <option
            v-for="status in options"
            :key="status.slug"
            :value="status.slug"
          >
            {{ status.label }}
          </option>
        </select>
      </LaisField>
      <LaisField
        v-if="canceling"
        :label="t('REAL_ESTATE_FINANCE.STATUS.CANCELED_ON')"
        required
      >
        <input
          v-model="canceledOn"
          type="date"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
        />
      </LaisField>
      <LaisField
        :label="
          canceling
            ? t('REAL_ESTATE_FINANCE.STATUS.CANCEL_REASON')
            : t('REAL_ESTATE_FINANCE.STATUS.OBSERVATION')
        "
        :required="canceling"
      >
        <textarea
          v-model="reason"
          rows="3"
          class="rounded-lais border border-lais-border px-3 py-2 text-sm"
          :placeholder="
            canceling
              ? t('REAL_ESTATE_FINANCE.STATUS.CANCEL_REASON_PLACEHOLDER')
              : t('REAL_ESTATE_FINANCE.STATUS.OBSERVATION_PLACEHOLDER')
          "
        />
      </LaisField>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" size="lg" @click="emit('close')">
          {{
            canceling
              ? t('REAL_ESTATE_FINANCE.STATUS.CANCEL_BACK')
              : t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE')
          }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :icon="canceling ? 'i-lucide-check' : ''"
          :class="canceling ? '!bg-lais-danger hover:!bg-lais-danger/90' : ''"
          :disabled="!valid"
          :is-loading="loading"
          size="lg"
          @click="confirm"
        >
          {{
            canceling
              ? t('REAL_ESTATE_FINANCE.STATUS.CANCEL')
              : t('REAL_ESTATE_FINANCE.STATUS.SAVE')
          }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
